/** Machine-readable guide (GET /api/agent): conventions, every route with its scope, work queues, procedures.
 * No bot prompts here, only facts about the API. */
import { useQuery } from "@tanstack/react-query";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { PageHeader } from "../../components/PageHeader";
import { ClientMdButtons, CopyButton, Empty, QueryView, Section, TableScroll } from "../lib/ui";
import { useUrlState } from "../lib/url";
import { RolesSection } from "./RoleBriefView";

type Guide = Schemas["AgentGuide"];
type RouteDoc = Schemas["RouteDoc"];

export function filterRoutes(routes: RouteDoc[], q: string, owner: string): RouteDoc[] {
  const needle = q.trim().toLowerCase();
  return routes.filter((r) =>
    (!owner || r.owner === owner)
    && (!needle || `${r.method} ${r.path} ${r.summary} ${r.scope ?? ""}`.toLowerCase().includes(needle)));
}

export function agentMarkdown(g: Guide): string {
  const out = ["# Agent guide", "", "## Conventions", ""];
  for (const [k, v] of Object.entries(g.conventions)) out.push(`- **${k}**: ${v}`);
  out.push("", "## Work queues", "", ...g.queues.map((q) => `- ${q}`), "", "## Procedures", "");
  for (const p of g.procedures) {
    out.push(`### ${p.name}`, "", ...p.steps.map((s, i) => `${i + 1}. ${s}`), "");
  }
  out.push("## Routes", "", "| method | path | scope | owner | summary |", "|---|---|---|---|---|");
  for (const r of g.routes) out.push(`| ${r.method} | \`${r.path}\` | ${r.scope ?? "public"} | ${r.owner ?? ""} | ${r.summary.replace(/\|/g, "\\|")} |`);
  return `${out.join("\n")}\n`;
}

export function AgentView() {
  const announce = useAnnounce();
  const { sp, set } = useUrlState();
  const q = sp.get("q") ?? "";
  const owner = sp.get("owner") ?? "";
  const guide = useQuery({ queryKey: qk.list("agent"), queryFn: () => unwrap(api.GET("/api/agent")), staleTime: 300_000 });
  const owners = [...new Set((guide.data?.routes ?? []).map((r) => r.owner ?? "").filter(Boolean))].sort();
  return (
    <div className="ws7-page" data-testid="agent-page">
      <PageHeader title="Agent guide">
        <ClientMdButtons filename="agent-guide.md" markdown={guide.data ? agentMarkdown(guide.data) : null} />
      </PageHeader>
      <RolesSection />
      <QueryView q={guide} what="the agent guide">
        {(g) => (
          <>
            <Section id="conventions" title="Conventions">
              <dl className="ws7-kv" data-testid="conventions">
                {Object.entries(g.conventions).map(([k, v]) => (
                  <div key={k} style={{ display: "contents" }}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            </Section>
            <Section id="queues" title="Work queues">
              <ul className="ws7-chips" aria-label="Work queues">
                {g.queues.map((name) => <li key={name} className="chip ws7-mono">GET /api/work/next?queue={name}</li>)}
              </ul>
            </Section>
            <Section id="procedures" title="Procedures">
              <div className="ws7-proc">
                {g.procedures.map((p) => (
                  <div key={p.name}>
                    <h3>{p.name}</h3>
                    <ol>{p.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
                  </div>
                ))}
              </div>
            </Section>
            <Section id="routes" title={`Routes (${g.routes.length})`}>
              <form className="ws7-toolbar" role="search" aria-label="Filter routes" onSubmit={(e) => e.preventDefault()}>
                <label className="ws7-field">
                  Filter routes
                  <input type="search" value={q} data-testid="routes-q" onChange={(e) => set({ q: e.target.value }, { replace: true })} />
                </label>
                <label className="ws7-field">
                  Owner
                  <select value={owner} data-testid="routes-owner" onChange={(e) => {
                    set({ owner: e.target.value });
                    announce(`Routes of ${e.target.value || "every owner"}`);
                  }}>
                    <option value="">Every owner</option>
                    {owners.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              </form>
              <RoutesTable routes={filterRoutes(g.routes, q, owner)} />
            </Section>
          </>
        )}
      </QueryView>
    </div>
  );
}

function RoutesTable({ routes }: { routes: RouteDoc[] }) {
  if (routes.length === 0) return <Empty>No routes match.</Empty>;
  return (
    <TableScroll label="Route table">
      <table className="ws7-table" aria-label="Routes" data-testid="routes-table">
        <thead>
          <tr>
            <th scope="col">Method</th><th scope="col">Path</th><th scope="col">Scope</th><th scope="col">Owner</th>
            <th scope="col">Summary</th><th scope="col">Copy</th>
          </tr>
        </thead>
        <tbody>
          {routes.map((r) => {
            const sig = `${r.method} ${r.path}`;
            return (
              <tr key={sig}>
                <td className="ws7-method">{r.method}</td>
                <td className="ws7-mono">{r.path}</td>
                <td>{r.scope ?? "public"}</td>
                <td>{r.owner ?? ""}</td>
                <td>{r.summary}</td>
                <td>
                  <CopyButton text={sig} label="Copy" ariaLabel={`Copy ${sig}`} testId={`copy-route-${r.method}-${r.path}`} what={sig} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableScroll>
  );
}
